import React, { useState, useEffect, useRef } from 'react';
import { array, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { uploadGalleryAssetsHOC } from '../../util/contextHelpers';
import { propTypes } from '../../util/types';
import { Form, UnsavedChangesWrapper } from '../../components';
import EditListingPhotosPageThree from './EditListingPhotosPageThree';
import EditListingPhotosPageTwo from './EditListingPhotosPageTwo';
import EditListingPhotosPageOne from './EditListingPhotosPageOne';

import {
    PageTracker,
    BottomNavigation,
    BottomSeperator,
} from '../../components/EditListingWizard/Components';

import css from './EditListingPhotosForm.css';
import OptionalBottomNavButtons from './OptionalBottomNavButtons';

const EditListingPhotosForm = props => {
    const {
        images = [],
        assets,
        uploadAssetsInProgress,
        initialValues,
        currentListing,
        onSubmit,
        setCurrentFormInstance,
        onAssetsRequest,
        onImageUploadHandler: onImageUploadHandlerProp,
        onArrangingGalleryImages,
        onRemovingGalleryAssets,
    } = props;

    const [, setSubmittedImages] = useState([]);
    const [dragOverFORM, setDragOverFORM] = useState(false);
    const [imageIdDragged, setImageIdDragged] = useState(null);
    const [imageUploadRequested, setImageUploadRequested] = useState(false);
    const imageUploadLockRef = useRef(false);

    const { uuid: listingId } = currentListing.id;

    useEffect(() => {
        onAssetsRequest(listingId);
    }, [listingId]);

    const onImageUploadHandler = async (files, values) => {
        // every upload path (drop, file input, camera, Facebook) ends up here
        if (imageUploadLockRef.current || uploadAssetsInProgress) return;

        imageUploadLockRef.current = true;
        setImageUploadRequested(true);

        try {
            const { isSuccessful, hasVideo } = await onImageUploadHandlerProp({
                files,
                images,
                entityId: listingId,
            });

            if (isSuccessful) {
                onSubmit({
                    abortRedirect: true,
                    isAutoSave: true,
                    hasGalleryAssets: true,
                    hasVideo,
                    ...values,
                    mainListingImage: Date.now(),
                });
            }
        } finally {
            imageUploadLockRef.current = false;
            setImageUploadRequested(false);
        }
    };

    const onFormDragHandler = (e, flag) => {
        // function responsible for changing label background
        e.preventDefault();
        e.stopPropagation();

        // do not highlight if new images are dragged over an existing one;
        if (imageIdDragged) return;

        setDragOverFORM(flag);
    };

    const onFormDropHandler = (e, values) => {
        setDragOverFORM(false);

        const droppedFiles = e.dataTransfer && e.dataTransfer.files;

        if (!droppedFiles) return;

        onImageUploadHandler(droppedFiles, values);
    };

    const noAssets = !uploadAssetsInProgress && (!images || images.length === 0);

    return (
        <UnsavedChangesWrapper>
            <FinalForm
                {...props}
                keepDirtyOnReinitialize
                onImageUploadHandler={onImageUploadHandler}
                imageUploadRequested={imageUploadRequested}
                initialValues={{
                    ...initialValues.otherFields,
                }}
                render={fieldRenderProps => {
                    const {
                        form,
                        className,
                        fetchErrors,
                        imageUploadRequested,
                        intl,
                        disabled,
                        invalid,
                        onImageUploadHandler,
                        updateInProgress,
                        externalListing,
                        tabParams,
                        handleStepChange,
                        currentStep,
                        values,
                        currentListing,
                        isNewListingFlow,
                        errors,
                    } = fieldRenderProps;

                    const { updateListingError } = fetchErrors || {};

                    const submitInProgress = updateInProgress || uploadAssetsInProgress;

                    const submitCondition =
                        currentStep === 1
                            ? submitInProgress || noAssets
                            : invalid || disabled || submitInProgress || imageUploadRequested;
                    const submitDisabled = externalListing ? false : submitCondition;

                    const classes = classNames(css.root, className);

                    const showPreviewListingBtn =
                        currentListing && currentListing.id && isNewListingFlow;
                    const { allStepsCount } = tabParams;

                    return (
                        <Form
                            className={classes}
                            onSubmit={e => {
                                e.preventDefault();
                                setSubmittedImages(images);
                                onSubmit(values);
                            }}
                            onDragOver={e => onFormDragHandler(e, true)}
                            onDragEnter={e => onFormDragHandler(e, true)}
                            onDragLeave={e => onFormDragHandler(e, false)}
                            onDragEnd={e => onFormDragHandler(e, false)}
                            onDrop={e => onFormDropHandler(e, values)}
                        >
                            {updateListingError ? (
                                <p className={css.error}>
                                    <FormattedMessage id="EditListingPhotosForm.updateFailed" />
                                </p>
                            ) : null}

                            <FormSpy
                                onChange={formState => {
                                    setCurrentFormInstance({
                                        values: formState.values,
                                        valid: formState.valid,
                                        dirty: formState.dirty,
                                        handleSubmit: props.onSubmit,
                                    });
                                }}
                                subscription={{ values: true, dirty: true, valid: true }}
                            />

                            <PageTracker
                                allStepsCount={allStepsCount}
                                currentStep={currentStep}
                                titleMsgId="EditListingPhotosForm.pagesTitle"
                            />
                            {currentStep === 1 ? (
                                <EditListingPhotosPageOne
                                    imageUploadRequested={imageUploadRequested}
                                    dragOverFORM={dragOverFORM}
                                    setImageIdDragged={setImageIdDragged}
                                    uploadAssetsInProgress={uploadAssetsInProgress}
                                    setImageUploadRequested={setImageUploadRequested}
                                    onArrangingGalleryImages={onArrangingGalleryImages}
                                    onRemovingGalleryAssets={onRemovingGalleryAssets}
                                    onImageUploadHandler={onImageUploadHandler}
                                    currentListing={currentListing}
                                    images={images}
                                    assets={assets}
                                    values={values}
                                    onSubmit={onSubmit}
                                    fetchErrors={fetchErrors}
                                    submitInProgress={submitInProgress}
                                    noAssets={noAssets}
                                    form={form}
                                />
                            ) : null}

                            {currentStep == 2 ? (
                                <EditListingPhotosPageTwo
                                    intl={intl}
                                    form={form}
                                    currentListing={currentListing}
                                    values={values}
                                />
                            ) : null}

                            {currentStep == 3 ? (
                                <EditListingPhotosPageThree
                                    values={values}
                                    currentListing={currentListing}
                                    intl={intl}
                                    form={form}
                                />
                            ) : null}

                            <BottomSeperator showPreviewListingBtn={showPreviewListingBtn} />

                            <BottomNavigation
                                allStepsCount={allStepsCount}
                                currentStep={currentStep}
                                tabNum={3}
                                handleStepChange={handleStepChange}
                                submitInProgress={submitInProgress}
                                submitDisabled={submitDisabled}
                                values={values}
                                currentListing={currentListing}
                                isNewListingFlow={isNewListingFlow}
                                showPreviewListingBtn={showPreviewListingBtn}
                                onSubmit={onSubmit}
                                customButtons={
                                    currentStep === 3 && isNewListingFlow
                                        ? () => (
                                              <OptionalBottomNavButtons
                                                  pristine={Boolean(values.description)}
                                                  inProgress={submitInProgress}
                                                  disabled={submitDisabled || errors?.description}
                                                  goBack={() =>
                                                      handleStepChange({
                                                          action: 'previous',
                                                          tabNum: 3,
                                                      })
                                                  }
                                              />
                                          )
                                        : null
                                }
                            />
                        </Form>
                    );
                }}
            />
        </UnsavedChangesWrapper>
    );
};

EditListingPhotosForm.propTypes = {
    fetchErrors: shape({
        publishListingError: propTypes.error,
        showListingsError: propTypes.error,
        uploadImageError: propTypes.error,
        updateListingError: propTypes.error,
    }),
    images: array,
    intl: intlShape.isRequired,

    onSubmit: func.isRequired,
    saveActionMsg: string.isRequired,
    updated: bool.isRequired,

    updateInProgress: bool.isRequired,
    externalListing: bool,
};

export default compose(injectIntl)(uploadGalleryAssetsHOC(EditListingPhotosForm));
